import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn';

/**
 * InputField variants using CVA (Class Variance Authority)
 * Defines all visual variations of the InputField component
 */
const inputFieldVariants = cva(
  // Base styles (always applied)
  [
    'flex-1 min-w-[200px] w-full',
    'border-2 rounded-linkode',
    'font-mono',
    'bg-white/80',
    'outline-none',
    'transition-all duration-200',
    'focus:bg-white focus:ring-2 focus:ring-linkode-teal',
    'disabled:cursor-not-allowed disabled:opacity-50',
  ],
  {
    variants: {
      inputSize: {
        sm: 'px-3 py-2 text-sm',
        md: 'px-4 py-3 text-base',
        lg: 'px-5 py-4 text-lg',
      },
      hasError: {
        true: 'border-linkode-red focus:ring-linkode-red',
        false: 'border-linkode-text',
      },
    },
    defaultVariants: {
      inputSize: 'md',
      hasError: false,
    },
  }
);

/** Exported type for InputField variants */
export type InputFieldVariants = VariantProps<typeof inputFieldVariants>;

/**
 * Simplified change handler that receives just the value string.
 * This is the preferred API for most use cases.
 */
export type SimpleChangeHandler = (value: string) => void;

export interface InputFieldProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange'>,
    InputFieldVariants {
  /** Input type */
  type?: 'text' | 'email' | 'password';
  /**
   * Change handler - supports both simplified (value: string) => void
   * and standard React.ChangeEventHandler<HTMLInputElement>
   */
  onChange?: SimpleChangeHandler | React.ChangeEventHandler<HTMLInputElement>;
  /** Error message to display */
  errorMessage?: string;
}

/** Input field component following Linkode Landing Page design */
export const InputField: React.FC<InputFieldProps> = ({
  inputSize,
  hasError,
  errorMessage,
  className,
  type = 'text',
  onChange,
  ...props
}) => {
  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    if (!onChange) return;

    // Check if onChange expects a string (simplified API) or event (standard API)
    // by checking the function's length (number of expected arguments)
    // or by trying to call it with the value first
    if (onChange.length === 1) {
      // Could be either (value: string) or (event: ChangeEvent)
      // We'll try to detect by checking if it's a setState function pattern
      try {
        (onChange as SimpleChangeHandler)(e.target.value);
      } catch {
        (onChange as React.ChangeEventHandler<HTMLInputElement>)(e);
      }
    } else {
      // Standard event handler
      (onChange as React.ChangeEventHandler<HTMLInputElement>)(e);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <input
        type={type}
        className={cn(inputFieldVariants({ inputSize, hasError }), className)}
        aria-invalid={hasError || undefined}
        onChange={handleChange}
        {...props}
      />
      {hasError && errorMessage && <span className="text-sm text-linkode-red">{errorMessage}</span>}
    </div>
  );
};

// Export inputFieldVariants for external use (e.g., testing)
export { inputFieldVariants };
